<script setup lang="ts">
import { z } from 'zod'
import { Form } from '@primevue/forms'
import { useToast } from '@/shared/composables/UseToast'
import { zodResolver } from '@primeuix/forms/resolvers/zod'
import {
  CreateCompanyFromAdminAdminCreateCompanyDto,
  CreateCompanyFromAdminCreationType,
} from '@swipe4work/models-orval'
import { createAdminCompany } from '@swipe4work/api-orval'
import InternalEmployerSelect from '@/shared/components/form/InternalEmployerSelect.vue'
import RelinkableCompanySelect from '@/shared/components/form/RelinkableCompanySelect.vue'
import { useDynamicDialogLifecycle } from '@/shared/composables/UseDynamicDialogLifecycle'

const { closeDialog, dialogData } = useDynamicDialogLifecycle<{
  creationType?: CreateCompanyFromAdminCreationType
}>()

const toast = useToast()

const isSubmitting = ref<boolean>(false)

async function onFormSubmit({ values, valid }: {
  values: any | undefined,
  valid: boolean
}) {
  if (!valid || !values) {
    toast.error('Validatiefout', 'Er zijn nog validatiefouten in het formulier')

    return
  }

  isSubmitting.value = true

  const req : CreateCompanyFromAdminAdminCreateCompanyDto = {
    name: values.name,
    creationType: values.creationType,
    employer: {
      email: values.leadEmail,
      firstName: values.leadFirstName,
      lastName: values.leadLastName,
    },
    accountManagerUuid: values.accountManagerUuid,
    salesPersonUuid: values.salesPersonUuid ?? null,
    relinkCompanyId: values.relinkCompanyId ?? null,
    plan: {
      invoice: values.invoiceDetails?.email
        ? {
          email: values.invoiceDetails?.email,
          address: {
            postalCode: values.invoiceDetails?.postalCode,
            addressLine: values.invoiceDetails?.addressLine,
            city: values.invoiceDetails?.city,
          },
        }
        : undefined,
      matchCredits: values.matchCredits,
    },
  }

  createAdminCompany(req)
    .then((res) => {
      toast.success('Bedrijf aangemaakt', 'Het bedrijf is succesvol aangemaakt')
      closeDialog()
      useRouter().push({
        name: 'companies.[id]',
        params: { id: res.id }, 
      })
    })
    .catch(() => {
      toast.error('Error', 'Er is iets misgegaan bij het aanmaken van het bedrijf')
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

const resolver = zodResolver(
  z.object({
    creationType: z.nativeEnum(CreateCompanyFromAdminCreationType),
    name: z.string({
      required_error: 'Bedrijfsnaam is verplicht',
    }).min(3, 'Bedrijfsnaam is verplicht'),
    leadEmail: z.string({
      required_error: 'E-mailadres is verplicht',
    }).email('Ongeldig e-mailadres'),
    leadFirstName: z.string({
      required_error: 'Voornaam is verplicht',
    }),
    leadLastName: z.string({
      required_error: 'Achternaam is verplicht',
    }),
    accountManagerUuid: z.string({
      required_error: 'Accountmanager is verplicht',
    }),
    salesPersonUuid: z.string().optional().nullable(),
    invoiceDetails: z.object({
      email: z.string().optional(),
      postalCode: z.string().optional(),
      addressLine: z.string().optional(),
      city: z.string().optional(),
    }).optional(),
    matchCredits: z.number().optional(),
    relinkCompanyId: z.number().nullable().optional(),
    reactivationConfirmed: z.boolean().optional(),
  }).required({
    creationType: true,
    name: true,
    leadEmail: true,
    leadFirstName: true,
    leadLastName: true,
    accountManagerUuid: true,
  }).superRefine((data, ctx) => {
    /*
     * A Match-Credits company creates a Teamleader account, so force a confirmation that the sender
     * checked for an existing (deleted) account to reactivate before creating a new one.
     */
    if (data.creationType === CreateCompanyFromAdminCreationType.MatchCredits && data.reactivationConfirmed !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [ 'reactivationConfirmed' ],
        message: 'Bevestig dat je hebt gecontroleerd op een bestaand account',
      })
    }
  }),
)

const companyTypes = [
  {
    label: 'Match-Credits',
    value: CreateCompanyFromAdminCreationType.MatchCredits,
  },
  {
    label: 'Lead',
    value: CreateCompanyFromAdminCreationType.Lead,
  },
]
</script>

<template>
  <section class="min-w-90vw flex flex-col gap-y-7.5 lg:min-w-3xl md:min-w-xl">
    <h2 class="mt-5">
      Nieuw bedrijf toevoegen
    </h2>

    <Form
      :resolver
      v-slot="$form"
      @submit="onFormSubmit"
      class="w-full flex flex-col gap-4"
      :initial-values="{
        creationType: dialogData?.creationType,
      }"
    >
      <FormField
        v-slot="$field"
        name="creationType"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <Select
            class="w-full"
            :options="companyTypes"
            option-label="label"
            option-value="value"
          />
          <label for="name">Type</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="name"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InputText class="w-full" />
          <label for="name">Bedrijfsnaam</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="accountManagerUuid"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InternalEmployerSelect
            value-field="externalId"
            :department="['CustomerSuccess', 'Sales']"
            fallback-to-all
            placeholder=""
          />
          <label for="accountManagerUuid">Accountmanager</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <FormField
        v-slot="$field"
        name="salesPersonUuid"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InternalEmployerSelect
            value-field="externalId"
            :department="['CustomerSuccess', 'Sales']"
            fallback-to-all
            placeholder=""
          />
          <label for="salesPersonUuid">Sales persoon (optioneel)</label>
        </IftaLabel>

        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <h3>
        Contactpersoon
      </h3>

      <div class="grid grid-cols-2 gap-x-2">
        <FormField
          v-slot="$field"
          name="leadFirstName"
          as-child
          class="flex flex-col gap-1"
        >
          <div>
            <IftaLabel>
              <InputText class="w-full" />
              <label for="leadFirstName">Voornaam</label>
            </IftaLabel>

            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </div>
        </FormField>

        <FormField
          v-slot="$field"
          name="leadLastName"
          class="flex flex-col gap-1"
        >
          <div>
            <IftaLabel>
              <InputText class="w-full" />
              <label for="leadLastName">Achternaam</label>
            </IftaLabel>

            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </div>
        </FormField>
      </div>

      <FormField
        v-slot="$field"
        name="leadEmail"
        class="flex flex-col gap-1"
      >
        <IftaLabel>
          <InputText class="w-full" />
          <label for="leadEmail">Email</label>
        </IftaLabel>
        <Message
          v-if="$field?.invalid"
          severity="error"
          size="small"
          variant="simple"
        >
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <template v-if="$form.creationType?.value === CreateCompanyFromAdminCreationType.MatchCredits || $form.type?.value === CreateCompanyFromAdminCreationType.JobSlots">
        <h3>
          Factuuratiegegevens
        </h3>

        <FormField
          v-slot="$field"
          name="invoiceDetails.email"
          class="flex flex-col gap-1"
        >
          <IftaLabel>
            <InputText class="w-full" />
            <label for="invoiceDetails.email">E-mailadres</label>
          </IftaLabel>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField
          v-slot="$field"
          name="invoiceDetails.addressLine"
          class="flex flex-col gap-1"
        >
          <IftaLabel>
            <InputText class="w-full" />
            <label for="invoiceDetails.addressLine">Adres</label>
          </IftaLabel>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <div class="grid grid-cols-2 gap-x-2">
          <FormField
            v-slot="$field"
            name="invoiceDetails.postalCode"
            class="flex flex-col gap-1"
          >
            <IftaLabel>
              <InputText class="w-full" />
              <label for="invoiceDetails.postalCode">Postcode</label>
            </IftaLabel>
            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </FormField>

          <FormField
            v-slot="$field"
            name="invoiceDetails.city"
            class="flex flex-col gap-1"
          >
            <IftaLabel>
              <InputText class="w-full" />
              <label for="invoiceDetails.city">Woonplaats</label>
            </IftaLabel>
            <Message
              v-if="$field?.invalid"
              severity="error"
              size="small"
              variant="simple"
            >
              {{ $field.error?.message }}
            </Message>
          </FormField>
        </div>
      </template>

      <template v-if="$form.creationType?.value === CreateCompanyFromAdminCreationType.MatchCredits">
        <h3>
          Bedrijfsplan (quota)
        </h3>

        <FormField
          v-slot="$field"
          name="matchCredits"
          class="flex flex-col gap-1"
        >
          <IftaLabel>
            <InputNumber class="w-full" :min="0" />
            <label for="matchCredits">Aantal match-credits</label>
          </IftaLabel>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>
      </template>

      <template v-if="$form.creationType?.value === CreateCompanyFromAdminCreationType.MatchCredits">
        <h3>
          Heractivatie
        </h3>

        <FormField
          v-slot="$field"
          name="relinkCompanyId"
          class="flex flex-col gap-1"
        >
          <IftaLabel>
            <RelinkableCompanySelect placeholder="" />
            <label for="relinkCompanyId">Bestaand Teamleader-account koppelen (optioneel)</label>
          </IftaLabel>
          <p class="text-xs text-surface-400">
            Kies een eerder verwijderd bedrijf om het bestaande Teamleader-account te hergebruiken in
            plaats van een nieuw account aan te maken.
          </p>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>

        <FormField
          v-slot="$field"
          name="reactivationConfirmed"
          class="flex flex-col gap-1"
        >
          <div class="flex items-start gap-2">
            <Checkbox input-id="reactivationConfirmed" binary :invalid="$field?.invalid" />
            <label for="reactivationConfirmed" class="text-sm">
              Ik heb gecontroleerd of er al een (verwijderd) account bestond voor heractivatie en dat dit niet het geval was.
            </label>
          </div>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $field.error?.message }}
          </Message>
        </FormField>
      </template>

      <div class="flex justify-end gap-x-3">
        <Button
          label="Sluiten"
          severity="secondary"
          @click="closeDialog"
          :disabled="isSubmitting"
        />
        <Button
          type="submit"
          label="Versturen"
        />
      </div>
    </Form>
  </section>
</template>
